/**
 * Erros da API traduzidos para o front.
 *
 * O back responde todo erro esperado no mesmo envelope
 * (`core/errors.py` e os `presentation/errors.py` de cada contexto):
 *
 *   { "error": { "code": "already_enrolled", "message": "...", ...contexto } }
 *
 * Validação do Pydantic chega no formato do FastAPI (`{ detail: [...] }`)
 * e é normalizada para o mesmo shape.
 */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly context: Record<string, unknown>;
  readonly fields: FieldError[];

  constructor(
    status: number,
    code: string,
    message: string,
    context: Record<string, unknown> = {},
    fields: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.context = context;
    this.fields = fields;
  }

  static from(status: number, body: unknown): ApiError {
    if (isRecord(body) && isRecord(body.error)) {
      const { code, message, ...context } = body.error;
      return new ApiError(status, String(code ?? 'app_error'), String(message ?? 'Erro inesperado'), context);
    }
    if (isRecord(body) && Array.isArray(body.detail)) {
      const fields = body.detail.filter(isRecord).map((d) => ({
        field: Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '',
        message: String(d.msg ?? ''),
      }));
      return new ApiError(status, 'validation_error', 'Dados inválidos', {}, fields);
    }
    if (status === 0) return new ApiError(0, 'network_error', 'Sem conexão com o servidor');
    return new ApiError(status, `http_${status}`, 'Erro inesperado no servidor');
  }

  is(...codes: string[]): boolean {
    return codes.includes(this.code);
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Mensagem pronta para a tela, sem vazar detalhe técnico. */
export function errorMessage(e: unknown): string {
  if (isApiError(e)) return e.message;
  return 'Algo deu errado. Tente novamente.';
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
